import { HttpClient, HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { buildUrl, isDefined } from '@trimble-gcs/common';
import {
  catchError,
  combineLatest,
  EMPTY,
  filter,
  map,
  Observable,
  retry,
  switchMap,
  take,
} from 'rxjs';
import { AppRoute } from '../app-route';
import { SetProjectRegion } from '../app-state/app.actions';
import { AppState } from '../app-state/app.state';
import { AuthState } from '../auth/auth.state';
import { injectLogger } from '../logging/logger-injection';
import { Project } from '../tenancy/tenancy.models';
import { TenancyService } from '../tenancy/tenancy.service';
import { ProjectRegion } from './project-region.models';

interface ProjectRegionDto {
  sdsRegionCode: string;
  sdsBaseUrl: string;
  featuresUrl: string;
}

@Injectable({
  providedIn: 'root',
})
export class ProjectRegionService {
  private readonly http = inject(HttpClient);
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly tenancyService = inject(TenancyService);
  private readonly logger = injectLogger('ProjectRegionService');

  loadProjectRegion(): Observable<void> {
    const projectsUrl = this.store.selectSnapshot(AppState.settings).endpoints.projects.url;

    const project$ = this.store.select(AppState.project).pipe(filter(isDefined));
    const loggedIn$ = this.store.select(AuthState.loggedIn).pipe(filter((loggedIn) => loggedIn));

    return combineLatest([project$, loggedIn$]).pipe(
      take(1),
      switchMap(([connectProject]) => {
        const url = buildUrl(projectsUrl, `/${connectProject.id}`);
        return this.http.get<ProjectRegionDto>(url);
      }),
      map((projectRegionDto) => {
        const projectRegion: ProjectRegion = {
          scandata: {
            url: projectRegionDto.sdsBaseUrl,
            protected: true,
            addClientHeaders: true,
          },
          features: {
            url: projectRegionDto.featuresUrl,
            protected: true,
          },
        };

        return projectRegion;
      }),
      switchMap((projectRegion) => {
        return this.store.dispatch(new SetProjectRegion(projectRegion));
      }),
      retry({
        count: 1,
        delay: (error: HttpErrorResponse) => this.shouldRetry(error),
      }),
      catchError((err: HttpErrorResponse) => {
        if (err.status === HttpStatusCode.Forbidden) {
          this.logger.error('NoAccess: User does not have access to fetch project.', {}, err);
          this.router.navigate([AppRoute.NoAccess]);
        }

        this.logger.error(err.message, {}, err);

        return EMPTY;
      }),
    );
  }

  private shouldRetry(err: HttpErrorResponse): Observable<Project> {
    if (err.status === HttpStatusCode.NotFound) {
      return this.tenancyService.createTenancy();
    }

    throw err;
  }
}
